<div class="modal-header">
  <h3 class="modal-title">{{ isStandalone ? 'Firmar Contrato' : 'Enviar a CLM Sign' }}</h3>
  <button type="button" class="close text-primary" aria-label="Close" (click)="activeModal.dismiss('Cross click')" [disabled]="loading">
    <i class="fas fa-times-circle"></i>
  </button>
</div>

<div class="modal-body lexgo-wizard">
  <nav class="lexgo-wizard__steps" aria-label="Pasos">
    @for (s of steps; track s.id) {
      <button
        type="button"
        class="lexgo-wizard__step"
        [class.lexgo-wizard__step--active]="step === s.id"
        [class.lexgo-wizard__step--done]="step > s.id"
        (click)="goToStep(s.id)"
        [disabled]="s.id > step"
      >
        <span class="lexgo-wizard__step-num">{{ s.id }}</span>
        <span class="lexgo-wizard__step-label">{{ s.label }}</span>
      </button>
    }
  </nav>

  @if (errorMessage) {
    <ngb-alert class="mt-2 mb-2" type="danger" (close)="errorMessage = ''">{{ errorMessage }}</ngb-alert>
  }
  @if (successMessage) {
    <ngb-alert class="mt-2 mb-2" type="success" (close)="successMessage = ''">{{ successMessage }}</ngb-alert>
  }

  <!-- Paso 1: Documento -->
  @if (step === 1) {
    <section class="lexgo-wizard__panel">
      <h4 class="lexgo-wizard__panel-title">
        {{ isStandalone ? 'Carga tus documentos en PDF para firmar' : 'Subir documento PDF' }}
      </h4>
      <p class="text-muted small mb-3">
        @if (isStandalone) {
          Sube tus documentos .pdf desde tu dispositivo. Opcionalmente podrás agregar una hoja al final para los firmantes.
        } @else {
          Elija un PDF del requerimiento o cargue uno nuevo para firmar con CLM Sign.
        }
      </p>

      <div class="form-group">
        <label>Documento PDF <span class="text-danger">*</span></label>

        @if (!isStandalone) {
          @if (loadingFiles) {
            <div class="text-muted small mb-2">Cargando documentos del requerimiento…</div>
          } @else if (attachments.length) {
            <label class="small font-weight-bold d-block">Usar documento existente</label>
            <select
              class="form-control mb-2"
              [(ngModel)]="selectedAttachmentId"
              (ngModelChange)="onExistingSelected()"
              name="attachmentId"
              [disabled]="!!uploadedFile"
            >
              <option [ngValue]="null">Seleccione un PDF</option>
              @for (file of attachments; track file.id) {
                <option [ngValue]="file.id">{{ file.filename || file.name }}</option>
              }
            </select>
          } @else {
            <div class="alert alert-light border small mb-2">
              No hay PDFs en el requerimiento. Puede cargar uno abajo.
            </div>
          }

          <div class="text-center text-muted small my-2">— o —</div>
          <label class="small font-weight-bold d-block">Cargar un PDF nuevo</label>
        }

        @if (!uploadedFile) {
          <div class="lexgo-wizard__dropzone">
            <app-attachment-to-sign
              (sendingFiles)="retrieveFiles($event)"
              [accept]="'application/pdf'"
              [hideNoteField]="true"
            >
            </app-attachment-to-sign>
          </div>
        } @else {
          <div class="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span class="small">
              <i class="fas fa-file-pdf text-danger mr-2"></i>
              {{ uploadedFile.name }}
            </span>
            <button type="button" class="btn btn-sm btn-outline-danger rounded-pill" (click)="clearUploadedFile()">
              <i class="fas fa-trash"></i> Quitar
            </button>
          </div>
        }
      </div>
    </section>
  }

  <!-- Paso 2: Firmantes -->
  @if (step === 2) {
    <section class="lexgo-wizard__panel">
      <h4 class="lexgo-wizard__panel-title">Datos de los firmantes</h4>
      <p class="text-muted small mb-3">
        Añade el tipo de firma, agrega los firmantes y enviaremos un correo a cada uno para firmar.
      </p>

      <div class="lexgo-wizard__sig-type mb-4">
        <div class="lexgo-wizard__sig-type-label">
          <i class="fas fa-signature mr-1"></i>
          ¿Qué tipo de firma necesitas?
        </div>
        <div class="lexgo-wizard__sig-products">
          <button
            type="button"
            class="lexgo-wizard__sig-product"
            [class.lexgo-wizard__sig-product--active]="electronicSignatureType === 'SIMPLE'"
            (click)="setElectronicSignatureType('SIMPLE')"
          >
            <div class="lexgo-wizard__sig-product-title">Firma Electrónica Simple</div>
            <div class="lexgo-wizard__sig-product-foot">
              <span class="lexgo-wizard__sig-product-badge">GRATIS</span>
              <i class="fas fa-external-link-alt"></i>
            </div>
          </button>
          <button
            type="button"
            class="lexgo-wizard__sig-product"
            [class.lexgo-wizard__sig-product--active]="electronicSignatureType === 'ADVANCED'"
            (click)="setElectronicSignatureType('ADVANCED')"
          >
            <div class="lexgo-wizard__sig-product-title">Firma Electrónica Avanzada</div>
            <div class="lexgo-wizard__sig-product-foot">
              <span class="lexgo-wizard__sig-product-price">por {{ advancedUnitPriceLabel }}</span>
              <i class="fas fa-external-link-alt"></i>
            </div>
          </button>
        </div>
      </div>

      <h5 class="lexgo-wizard__subsection-title">¿Quiénes firman el documento?</h5>
      <p class="text-muted small mb-3">
        Ingresa los detalles de las personas que firman. Se les enviará la invitación por correo.
      </p>

      <div class="lexgo-wizard__signer-card mb-3">
        <div class="lexgo-wizard__signer-card-label">
          <i class="fas fa-user mr-1"></i>
          Persona firmante
        </div>

        <div class="form-group mb-2">
          <label class="small text-uppercase text-muted mb-1">Ingresa email o nombre del firmante</label>
          <div class="input-group lexgo-wizard__user-search">
            <input
              type="text"
              class="form-control"
              [(ngModel)]="userSearchQuery"
              name="userSearchQuery"
              placeholder="Email o nombre"
              (keydown.enter)="$event.preventDefault(); searchAndFillUser()"
              [disabled]="loadingUsers"
            />
            <div class="input-group-append">
              <button
                type="button"
                class="btn btn-primary lexgo-wizard__search-btn"
                (click)="searchAndFillUser()"
                [disabled]="loadingUsers"
                title="Buscar usuario"
              >
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>
          @if (loadingUsers) {
            <small class="text-muted">Cargando usuarios…</small>
          }
          @if (userSearchMatches.length > 1) {
            <div class="list-group list-group-flush lexgo-wizard__user-matches mt-2">
              @for (u of userSearchMatches; track u.id) {
                <button
                  type="button"
                  class="list-group-item list-group-item-action py-2"
                  (click)="applyUser(u)"
                >
                  <div class="font-weight-semibold">{{ u.name || u.email }}</div>
                  <div class="text-muted small">{{ u.email }}</div>
                </button>
              }
            </div>
          }
          <button
            type="button"
            class="btn btn-link btn-sm px-0 mt-1"
            (click)="enableManualSignerForm()"
          >
            Haz click aquí para ingresar los datos manualmente
          </button>
        </div>

        @if (showManualSignerForm) {
          <form #form="ngForm" (ngSubmit)="createFields(form)" novalidate class="fields-inputs row">
            <div class="col-md-6 mb-2">
              <label>Nombres <span class="text-danger">*</span></label>
              <input class="form-control" name="name" [(ngModel)]="signerDraft.name" required />
            </div>
            <div class="col-md-6 mb-2">
              <label>Apellidos</label>
              <input class="form-control" name="lastname" [(ngModel)]="signerDraft.lastname" />
            </div>
            <div class="col-md-6 mb-2">
              <label>
                Email <span class="text-danger">*</span>
                @if (validEmailFormat) {
                  <span class="text-danger ml-1">Formato inválido</span>
                }
              </label>
              <input
                class="form-control"
                name="email"
                required
                [(ngModel)]="signerDraft.email"
                (blur)="validateEmailFormat(signerDraft.email)"
              />
            </div>
            <div class="col-md-6 mb-2">
              <label>Número de teléfono móvil (opcional)</label>
              <div class="input-group lexgo-phone">
                <ng-select
                  class="lexgo-phone__country"
                  name="phoneCountry"
                  [items]="phoneCountries"
                  bindValue="iso"
                  bindLabel="name"
                  [(ngModel)]="signerDraft.phoneCountry"
                  [clearable]="false"
                  [searchable]="true"
                  [appendTo]="'body'"
                >
                  <ng-template ng-label-tmp let-item="item">
                    <span class="lexgo-phone__opt">
                      <img [src]="item.flagUrl" [alt]="item.iso" width="24" height="18" />
                      <span>{{ item.dial }}</span>
                    </span>
                  </ng-template>
                  <ng-template ng-option-tmp let-item="item">
                    <span class="lexgo-phone__opt">
                      <img [src]="item.flagUrl" [alt]="item.iso" width="24" height="18" />
                      <span>{{ item.name }} {{ item.dial }}</span>
                    </span>
                  </ng-template>
                </ng-select>
                <input
                  class="form-control"
                  name="phoneLocal"
                  [(ngModel)]="signerDraft.phoneLocal"
                  placeholder="9 1234 5678"
                />
              </div>
            </div>

            <div class="col-12 mt-2 mb-1">
              <div class="font-weight-bold small text-uppercase text-muted">Campos adicionales</div>
            </div>
            <div class="col-md-6 mb-2">
              <label>Número de identificación</label>
              <input
                class="form-control"
                name="identification"
                [(ngModel)]="signerDraft.identification"
                placeholder="Identificación del firmante"
              />
              <small class="form-text text-muted">En Chile corresponde al RUT (Lexgo no usa el rótulo “RUT”).</small>
            </div>
            <div class="col-md-6 mb-2">
              <label>Firma a nombre de o representa a una empresa (opcional)</label>
              <input
                class="form-control"
                name="repId"
                [(ngModel)]="signerDraft.repId"
                placeholder="Razón social o nombre de fantasía"
              />
            </div>
            <div class="col-12 mb-3 d-flex justify-content-end">
              @if (editingIndex != null) {
                <button type="button" class="btn btn-outline-secondary rounded-pill mr-2" (click)="cancelEdit()">
                  Cancelar edición
                </button>
              }
              <button
                type="submit"
                class="btn btn-outline-primary rounded-pill text-dark font-weight-bold"
                [disabled]="!form.form.valid || !validateEmail(signerDraft.email)"
              >
                {{ editingIndex != null ? 'Guardar firmante' : '+ Agregar firmante' }}
              </button>
            </div>
          </form>
        }
      </div>

      @if (signingparties.length) {
        <table class="table table-sm table-bordered">
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Identificación</th>
              <th>Teléfono</th>
              <th>Empresa</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (party of signingparties; track party.email; let i = $index) {
              <tr [class.table-active]="editingIndex === i">
                <td>{{ i + 1 }}</td>
                <td>{{ party.name }} {{ party.lastname || '' }}</td>
                <td>{{ party.email }}</td>
                <td>{{ party.identification || '—' }}</td>
                <td>{{ party.phone || '—' }}</td>
                <td>{{ party.repId || '—' }}</td>
                <td class="text-right text-nowrap">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary rounded-pill mr-1"
                    (click)="editField(i); enableManualSignerForm()"
                    title="Editar firmante"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-dark rounded-pill"
                    (click)="removeField(i)"
                    title="Eliminar firmante"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      <div class="lexgo-wizard__advanced mt-3">
        <div class="lexgo-wizard__advanced-title">
          <i class="fas fa-cog mr-2"></i>
          Opciones avanzadas
        </div>
        <ul class="nav lexgo-wizard__advanced-tabs">
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              [class.active]="advancedTab === 'flow'"
              (click)="advancedTab = 'flow'"
            >
              Flujo de firma
            </button>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              [class.active]="advancedTab === 'position'"
              (click)="advancedTab = 'position'"
            >
              Posición y estilo
            </button>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              [class.active]="advancedTab === 'org'"
              (click)="advancedTab = 'org'"
            >
              Organización
            </button>
          </li>
        </ul>

        <div class="lexgo-wizard__advanced-body">
          @if (advancedTab === 'flow') {
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">¿Quieres que la firma sea en orden secuencial?</div>
                <div class="text-muted small">
                  Si no eliges esta opción, todos recibirán la invitación al mismo tiempo.
                </div>
              </div>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="lexgoSequential"
                  [(ngModel)]="sequential"
                  name="sequential"
                />
                <label class="custom-control-label" for="lexgoSequential">
                  {{ sequential ? 'Sí' : 'No' }}
                </label>
              </div>
            </div>
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">Idioma de preferencia</div>
                <div class="text-muted small">
                  Selecciona el idioma a utilizar en correos y flujo de firma.
                </div>
              </div>
              <select class="form-control lexgo-wizard__adv-control" [(ngModel)]="language" name="language">
                <option value="ES">Español</option>
                <option value="EN">English</option>
              </select>
            </div>
          }

          @if (advancedTab === 'position') {
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">¿Quieres elegir dónde posicionar cada firma?</div>
                <div class="text-muted small">
                  Por defecto se agregarán en una página adicional.
                </div>
              </div>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="lexgoPreferCustom"
                  [(ngModel)]="preferCustomPlacement"
                  (ngModelChange)="onPreferCustomPlacementChange()"
                  name="preferCustomPlacement"
                />
                <label class="custom-control-label" for="lexgoPreferCustom">
                  {{ preferCustomPlacement ? 'Sí' : 'No' }}
                </label>
              </div>
            </div>
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">¿Qué estilo de firmas quieres utilizar?</div>
                <div class="text-muted small">
                  NOTA: El estilo completo con QR y pie de firma es obligatorio cuando se firma en una página adicional.
                </div>
              </div>
              @if (placementMode === 'extraPage' || isAdvancedSignature) {
                <select class="form-control lexgo-wizard__adv-control" [ngModel]="'FULL'" name="signatureStyleLocked" disabled>
                  <option value="FULL">Firma con datos y QR</option>
                </select>
              } @else {
                <select
                  class="form-control lexgo-wizard__adv-control"
                  [(ngModel)]="signatureStyle"
                  (ngModelChange)="onSignatureStyleChange()"
                  name="signatureStyle"
                >
                  <option value="SIMPLE">Sólo dibujo de firma</option>
                  <option value="FULL">Firma con datos y QR</option>
                </select>
              }
            </div>
          }

          @if (advancedTab === 'org') {
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">Carpeta de destino</div>
                <div class="text-muted small">
                  Selecciona dónde se guardará el documento firmado y opcionalmente la hoja de evidencia.
                </div>
              </div>
              <div class="lexgo-wizard__folder-pick">
                <input
                  type="text"
                  class="form-control"
                  [ngModel]="archivePath"
                  name="archivePathDisplay"
                  readonly
                />
                <button
                  type="button"
                  class="btn btn-outline-primary lexgo-wizard__folder-btn"
                  (click)="openArchiveFolderPicker()"
                  title="Buscar carpeta"
                >
                  <i class="fas fa-search"></i>
                </button>
              </div>
            </div>
            <div class="lexgo-wizard__adv-row">
              <div>
                <div class="font-weight-bold">¿Guardar también la hoja de evidencia del proceso de firma?</div>
              </div>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="lexgoSaveEvidence"
                  [(ngModel)]="saveEvidenceSheet"
                  name="saveEvidenceSheet"
                />
                <label class="custom-control-label" for="lexgoSaveEvidence">
                  {{ saveEvidenceSheet ? 'Sí' : 'No' }}
                </label>
              </div>
            </div>
          }
        </div>
      </div>

      <div class="lexgo-wizard__option-card mt-3">
        <label class="font-weight-bold d-block mb-1">Mensaje para los firmantes (opcional)</label>
        <p class="text-muted small mb-2">
          Agrega un mensaje explicando qué deben firmar. Este mensaje se incluirá en el correo enviado a los firmantes.
        </p>
        <label class="small text-muted" for="lexgoSignerMessage">Mensaje</label>
        <textarea
          id="lexgoSignerMessage"
          class="form-control"
          rows="3"
          [(ngModel)]="signerMessage"
          name="signerMessage"
          [maxlength]="signerMessageMax"
          placeholder="Ej.: Por favor firma este contrato a la brevedad."
        ></textarea>
        <small class="text-muted">
          Máximo {{ signerMessageMax }} caracteres ({{ signerMessageLength }}/{{ signerMessageMax }})
        </small>
      </div>
    </section>
  }

  <!-- Paso 3: Posición (solo si eligió posicionar en el PDF) -->
  @if (step === 3) {
    <section class="lexgo-wizard__panel">
      <h4 class="lexgo-wizard__panel-title">Posición de firmas</h4>
      <p class="text-muted small mb-3">
        Arrastre cada firmante al documento. Estilo:
        <strong>
          {{ signatureStyleEffective === 'FULL' ? 'Firma con datos y QR' : 'Sólo dibujo de firma' }}
        </strong>
      </p>

      @if (loadingPdf) {
        <div class="text-muted small py-3">Cargando documento para previsualizar…</div>
      } @else if (pdfData) {
        <app-lexgosign-placement
          [pdfData]="pdfData"
          [signers]="signingparties"
          [placements]="placements"
          [signatureStyle]="signatureStyleEffective"
          (placementsChange)="onPlacementsChange($event)"
        ></app-lexgosign-placement>
      } @else {
        <div class="alert alert-warning small mb-0">
          No se pudo cargar el PDF. Vuelva al paso Documento o reintente.
          <button type="button" class="btn btn-sm btn-outline-primary ml-2" (click)="ensurePdfLoaded(true)">
            Reintentar
          </button>
        </div>
      }
    </section>
  }

  <!-- Paso 4: Revisar y enviar -->
  @if (step === 4) {
    <section class="lexgo-wizard__panel">
      <h4 class="lexgo-wizard__panel-title">Revisar y enviar</h4>
      <p class="text-muted small mb-3">Confirme que la información es correcta.</p>

      <dl class="lexgo-wizard__review row mb-0">
        <dt class="col-sm-4">Documento</dt>
        <dd class="col-sm-8">{{ documentLabel }}</dd>

        <dt class="col-sm-4">Tipo de firma</dt>
        <dd class="col-sm-8">
          @if (electronicSignatureType === 'ADVANCED') {
            Firma Electrónica Avanzada
          } @else {
            Firma Electrónica Simple
          }
        </dd>

        <dt class="col-sm-4">Orden</dt>
        <dd class="col-sm-8">{{ sequential ? 'Secuencial' : 'Paralelo (todos a la vez)' }}</dd>

        <dt class="col-sm-4">Idioma</dt>
        <dd class="col-sm-8">{{ language === 'EN' ? 'English' : 'Español' }}</dd>

        <dt class="col-sm-4">Posición</dt>
        <dd class="col-sm-8">
          @if (placementMode === 'extraPage') {
            Hoja adicional al final
          } @else {
            Posición personalizada ({{ placements.length }} marca(s))
          }
        </dd>

        <dt class="col-sm-4">Estilo</dt>
        <dd class="col-sm-8">
          {{ signatureStyleEffective === 'FULL' ? 'Firma con datos y QR' : 'Sólo dibujo de firma' }}
        </dd>

        <dt class="col-sm-4">Carpeta destino</dt>
        <dd class="col-sm-8">{{ archivePath || 'Contratos' }}</dd>

        <dt class="col-sm-4">Hoja de evidencia</dt>
        <dd class="col-sm-8">{{ saveEvidenceSheet ? 'Sí' : 'No' }}</dd>

        @if (signerMessage.trim()) {
          <dt class="col-sm-4">Mensaje</dt>
          <dd class="col-sm-8">{{ signerMessage }}</dd>
        }

        <dt class="col-sm-4">Firmantes ({{ signingparties.length }})</dt>
        <dd class="col-sm-8">
          <ul class="mb-0 pl-3">
            @for (p of signingparties; track p.email; let i = $index) {
              <li>
                {{ i + 1 }}. {{ p.name }} {{ p.lastname || '' }} &lt;{{ p.email }}&gt;
                @if (p.identification) {
                  · ID {{ p.identification }}
                }
                @if (p.phone) {
                  · {{ p.phone }}
                }
                @if (p.repId) {
                  · {{ p.repId }}
                }
              </li>
            }
          </ul>
        </dd>
      </dl>

      @if (isAdvancedSignature) {
        <div class="lexgo-wizard__payment mt-4">
          <h5 class="lexgo-wizard__payment-title">
            <i class="fas fa-credit-card mr-2"></i>
            Seleccionar forma de pago
          </h5>
          <p class="text-muted small mb-3">
            Para procesar tu pedido necesitamos que selecciones la forma de pago:
          </p>

          <div class="lexgo-wizard__payment-methods mb-3">
            <label class="lexgo-wizard__payment-radio">
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                [(ngModel)]="paymentMethod"
                (ngModelChange)="onPaymentMethodChange('card')"
              />
              Tarjeta de crédito
            </label>
            @if (paymentConfig?.manualBillingEnabled !== false) {
              <label class="lexgo-wizard__payment-radio">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="manual"
                  [(ngModel)]="paymentMethod"
                  (ngModelChange)="onPaymentMethodChange('manual')"
                />
                Cobro Manual
                <i
                  class="fas fa-info-circle text-muted ml-1"
                  title="El cobro se gestionará fuera de la plataforma"
                ></i>
              </label>
            }
          </div>

          @if (paymentMethod === 'card') {
            <div class="lexgo-wizard__payment-card mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="font-weight-bold small">Detalles de la tarjeta</span>
                <span class="lexgo-wizard__stripe-badge">Powered by <strong>stripe</strong></span>
              </div>
              @if (paymentConfig?.mock || !paymentConfig?.stripeConfigured) {
                <div class="alert alert-light border small mb-0">
                  Modo demo: el pago se simulará al enviar. Configure Stripe en el dominio para cobros reales.
                </div>
              } @else {
                <div id="lexgo-stripe-card" class="lexgo-wizard__stripe-element"></div>
              }
            </div>
          }

          <div class="lexgo-wizard__payment-costs mb-3">
            <div class="font-weight-bold small mb-2">Detalle de costos</div>
            @if (paymentLoading) {
              <div class="text-muted small">Calculando…</div>
            } @else if (paymentQuote) {
              <div class="lexgo-wizard__cost-row">
                <span>{{ paymentQuote.label }}</span>
                <span>${{ paymentQuote.unitPriceUsd | number: '1.2-2' }} USD</span>
              </div>
              <div class="lexgo-wizard__cost-row">
                <span>Número de documentos</span>
                <span>{{ paymentQuote.documentCount }}</span>
              </div>
              @if (paymentQuote.discountUsd > 0) {
                <div class="lexgo-wizard__cost-row text-success">
                  <span>Descuento</span>
                  <span>-${{ paymentQuote.discountUsd | number: '1.2-2' }} USD</span>
                </div>
              }
              <div class="lexgo-wizard__cost-row">
                <span>IVA / VAT ({{ (paymentQuote.vatRate * 100) | number: '1.0-0' }}%)</span>
                <span>${{ paymentQuote.vatUsd | number: '1.2-2' }} USD</span>
              </div>
              <div class="lexgo-wizard__cost-total">
                <span>Total</span>
                <strong>${{ paymentQuote.totalUsd | number: '1.2-2' }} USD</strong>
              </div>
            } @else if (paymentQuoteError) {
              <!-- Antes esto quedaba vacío y el botón habilitado: se pagaba un
                   importe que nunca se mostró. -->
              <div class="text-danger small">{{ paymentQuoteError }}</div>
            } @else {
              <div class="text-muted small">Costo no disponible.</div>
            }
          </div>

          <div class="lexgo-wizard__coupon">
            <label class="small font-weight-bold">¿Tienes un cupón de descuento?</label>
            <div class="d-flex align-items-start mt-1">
              <input
                type="text"
                class="form-control flex-grow-1 mr-2"
                [(ngModel)]="couponCode"
                name="couponCode"
                placeholder="Ej: PROMO20"
              />
              <button
                type="button"
                class="btn btn-outline-primary rounded-pill lexgo-wizard__coupon-btn flex-shrink-0"
                (click)="applyCoupon()"
                [disabled]="paymentLoading"
              >
                Aplicar
              </button>
            </div>
            @if (couponMessage) {
              <small
                class="d-block mt-1"
                [class.text-success]="couponMessage === 'Cupón aplicado'"
                [class.text-danger]="couponMessage === 'Cupón no válido'"
              >
                {{ couponMessage }}
              </small>
            }
          </div>
        </div>
      }
    </section>
  }
</div>

<div class="modal-footer lexgo-wizard__footer">
  <div class="lexgo-wizard__footer-left">
    @if (step > 1) {
      <button type="button" class="btn btn-outline-secondary rounded-pill" (click)="prevStep()" [disabled]="loading">
        ← Volver
      </button>
    } @else {
      <button type="button" class="btn btn-outline-secondary rounded-pill" (click)="closeModal()" [disabled]="loading">
        Cancelar
      </button>
    }
  </div>

  <div class="lexgo-wizard__footer-right">
    @if (step < 4) {
      <button type="button" class="btn btn-primary rounded-pill font-weight-bold" (click)="nextStep()" [disabled]="loading">
        Continuar →
      </button>
    } @else {
      <button
        type="button"
        class="btn btn-primary rounded-pill font-weight-bold"
        (click)="submit()"
        [disabled]="loading || !hasDocument || !puedeEnviarConPago"
      >
        @if (loading) {
          Enviando…
        } @else if (isStandalone) {
          Enviar a firma
        } @else {
          Enviar a CLM Sign
        }
      </button>
    }
  </div>
</div>
